import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";

import {
  advertisersApi,
  appsApi,
  campaignsApi,
  queryKeys,
} from "../../lib/api/resources";
import type {
  CampaignRequest,
  Placement,
  PricingModel,
} from "../../lib/api/types";
import { count, isoDate, money } from "../../lib/format";
import { useToast } from "../../app/toast";
import {
  Button,
  Card,
  ErrorState,
  Field,
  Input,
  NumberInput,
  Select,
  Skeleton,
} from "../../components/ui";

/**
 * Create or edit a campaign.
 *
 * <p>One form for both, because the fields are identical and two would drift. A new campaign is
 * always saved as a draft: launching is a separate act on the detail screen, so nothing starts
 * spending because a form was submitted.
 */
/// What a rewarded campaign can promise.
///
/// A closed list rather than free text (ADS-0042): a typo does not fail loudly,
/// it produces a campaign whose reward the app does not recognise.
///
/// Deliberately short. The app grants a reroll and nothing else — it never reads
/// this value, it only reacts to "a reward was earned". Adding an option here
/// without adding the grant in the app would promise a user something that never
/// arrives, which is the one failure this format must not have.
const REWARD_TYPES = [{ value: "REROLL", label: "Nuevo intento (REROLL)" }];

export function CampaignFormPage() {
  const activeApps = useQuery({
    queryKey: queryKeys.activeApps,
    queryFn: () => appsApi.active(),
  });
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const advertisers = useQuery({
    queryKey: queryKeys.advertisers({ size: 100 }),
    queryFn: () => advertisersApi.list({ size: 100 }),
  });

  const existing = useQuery({
    queryKey: queryKeys.campaign(id ?? ""),
    queryFn: () => campaignsApi.get(id!),
    enabled: editing,
  });

  const [form, setForm] = useState<CampaignRequest>({
    advertiserId: "",
    name: "",
    appId: "lottery-app",
    placement: "BANNER",
    pricingModel: "CPM",
    priceAmount: 300,
    budgetTotal: 15000,
    startDate: isoDate(new Date()),
    endDate: isoDate(new Date(Date.now() + 30 * 86_400_000)),
    priority: 10,
    weight: 1,
    // Seeded, not left undefined (ADS-0042). The inputs below display `?? 1`
    // and `?? 15`, but that fallback is only what the user *sees* — the form
    // state stayed undefined unless they retyped the field, so a REWARDED
    // campaign was submitted with two null terms and rejected by
    // campaign_reward_terms_check with no hint as to which field was missing.
    rewardType: "REROLL",
    rewardAmount: 1,
    rewardMinWatchSeconds: 15,
  });

  useEffect(() => {
    if (existing.data) {
      const campaign = existing.data;
      setForm({
        advertiserId: campaign.advertiserId,
        name: campaign.name,
        appId: campaign.appId,
        placement: campaign.placement,
        pricingModel: campaign.pricingModel,
        priceAmount: campaign.priceAmount,
        budgetTotal: campaign.budgetTotal,
        startDate: campaign.startDate,
        endDate: campaign.endDate,
        priority: campaign.priority,
        weight: campaign.weight,
        rewardType: campaign.rewardType,
        rewardAmount: campaign.rewardAmount,
        rewardMinWatchSeconds: campaign.rewardMinWatchSeconds,
      });
    }
  }, [existing.data]);

  const save = useMutation({
    mutationFn: (body: CampaignRequest) =>
      editing ? campaignsApi.update(id!, body) : campaignsApi.create(body),
    onSuccess: (campaign) => {
      toast.success(
        editing ? "Campaña actualizada" : "Campaña creada como borrador",
      );
      void queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.campaign(campaign.id),
      });
      navigate(`/campaigns/${campaign.id}`);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (editing && existing.isLoading) {
    return <Skeleton className="h-96" />;
  }

  if (editing && existing.isError) {
    return <ErrorState message={(existing.error as Error).message} />;
  }

  const update = <K extends keyof CampaignRequest>(
    key: K,
    value: CampaignRequest[K],
  ) => setForm((current) => ({ ...current, [key]: value }));

  /**
   * Rough reach at the contracted rate.
   *
   * <p>A planning hint, not a promise, and clearly separate from any figure the backend reports.
   * It exists so an operator notices before signing that a budget buys far more or far less
   * delivery than the advertiser expects.
   */
  const estimatedReach =
    form.pricingModel === "CPM" && form.priceAmount > 0
      ? Math.floor((form.budgetTotal / form.priceAmount) * 1000)
      : form.pricingModel === "CPC" && form.priceAmount > 0
        ? Math.floor(form.budgetTotal / form.priceAmount)
        : null;

  const datesInverted = form.endDate < form.startDate;

  return (
    <form
      className="flex max-w-3xl flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        // The server refuses reward terms on a non-rewarded placement, and refuses a rewarded
        // campaign without them. Switching placement mid-form would otherwise submit stale terms
        // and surface as a 400 the operator cannot explain.
        const rewarded = form.placement === "REWARDED";
        save.mutate({
          ...form,
          // Defaults applied here too. The server refuses a rewarded campaign
          // with any term missing, and its constraint error names none of them
          // — so a gap here surfaces as an unexplained failure.
          rewardType: rewarded ? form.rewardType?.trim() || "REROLL" : null,
          rewardAmount: rewarded ? (form.rewardAmount ?? 1) : null,
          rewardMinWatchSeconds: rewarded
            ? (form.rewardMinWatchSeconds ?? 15)
            : null,
        });
      }}
    >
      <h1 className="text-headline-lg text-text-primary">
        {editing ? "Editar campaña" : "Nueva campaña"}
      </h1>

      <Card title="Anunciante y ubicación">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Anunciante" htmlFor="advertiserId">
            <Select
              id="advertiserId"
              required
              value={form.advertiserId}
              onChange={(event) => update("advertiserId", event.target.value)}
            >
              <option value="">Selecciona un anunciante</option>
              {advertisers.data?.content.map((advertiser) => (
                <option key={advertiser.id} value={advertiser.id}>
                  {advertiser.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Nombre" htmlFor="name">
            <Input
              id="name"
              required
              maxLength={200}
              value={form.name}
              onChange={(event) => update("name", event.target.value)}
            />
          </Field>

          <Field
            label="App"
            htmlFor="appId"
            hint="Solo las apps registradas pueden pedir anuncios"
          >
            <Select
              id="appId"
              value={form.appId}
              onChange={(event) => update("appId", event.target.value)}
            >
              {/* An app that is not registered would never request this
                  campaign, so it is not offered (ADS-0043). */}
              {(activeApps.data ?? []).map((app) => (
                <option key={app.id} value={app.appId}>
                  {app.displayName}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Ubicación" htmlFor="placement">
            <Select
              id="placement"
              value={form.placement}
              onChange={(event) =>
                update("placement", event.target.value as Placement)
              }
            >
              <option value="BANNER">Banner (320×50)</option>
              <option value="INTERSTITIAL">
                Interstitial (mínimo 300×250)
              </option>
              <option value="REWARDED">Recompensado (video)</option>
              <option value="APP_OPEN">Apertura de app</option>
            </Select>
          </Field>
        </div>
      </Card>

      {form.placement === "REWARDED" && (
        <Card title="Recompensa">
          <p className="mb-4 text-body-md text-text-secondary">
            Lo que el usuario recibe por ver el video completo. El servidor
            verifica que el anuncio se haya mostrado y que haya pasado el tiempo
            mínimo antes de otorgarla.
          </p>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field
              label="Tipo"
              htmlFor="rewardType"
              hint="Lo único que la app sabe otorgar hoy es un nuevo intento."
            >
              <Select
                id="rewardType"
                value={form.rewardType ?? "REROLL"}
                onChange={(event) => update("rewardType", event.target.value)}
              >
                {REWARD_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Cantidad" htmlFor="rewardAmount">
              <NumberInput
                id="rewardAmount"
                required
                min={1}
                value={form.rewardAmount ?? 1}
                onChange={(event) =>
                  update("rewardAmount", Number(event.target.value))
                }
              />
            </Field>

            <Field
              label="Segundos mínimos"
              htmlFor="rewardMinWatchSeconds"
              hint="Tiempo que debe pasar desde que se sirve el anuncio para que cuente como visto."
            >
              <NumberInput
                id="rewardMinWatchSeconds"
                required
                min={0}
                value={form.rewardMinWatchSeconds ?? 15}
                onChange={(event) => {
                  const parsed = Number(event.target.value);
                  update(
                    "rewardMinWatchSeconds",
                    Number.isFinite(parsed) && parsed >= 0 ? parsed : 15,
                  );
                }}
              />
            </Field>
          </div>
        </Card>
      )}

      <Card title="Precio y presupuesto">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Modelo" htmlFor="pricingModel">
            <Select
              id="pricingModel"
              value={form.pricingModel}
              onChange={(event) =>
                update("pricingModel", event.target.value as PricingModel)
              }
            >
              <option value="CPM">CPM — por cada 1.000 impresiones</option>
              <option value="CPC">CPC — por clic válido</option>
              <option value="FIXED">Fijo — por todo el vuelo</option>
            </Select>
          </Field>

          <Field
            label="Precio"
            htmlFor="priceAmount"
            hint={
              form.pricingModel === "CPM"
                ? "Por cada 1.000 impresiones"
                : form.pricingModel === "CPC"
                  ? "Por clic válido"
                  : "Precio del vuelo completo"
            }
          >
            <NumberInput
              id="priceAmount"
              required
              min={0}
              step="0.01"
              value={form.priceAmount}
              onChange={(event) =>
                update("priceAmount", Number(event.target.value))
              }
            />
          </Field>

          <Field
            label="Presupuesto total"
            htmlFor="budgetTotal"
            hint={
              estimatedReach !== null
                ? `Alcance estimado: ${count(estimatedReach)} ${form.pricingModel === "CPM" ? "impresiones" : "clics"}`
                : undefined
            }
          >
            <NumberInput
              id="budgetTotal"
              required
              min={0}
              step="0.01"
              value={form.budgetTotal}
              onChange={(event) =>
                update("budgetTotal", Number(event.target.value))
              }
            />
          </Field>

          {editing && existing.data && (
            <Field
              label="Consumido"
              hint="Lo calcula el servidor. No es editable."
            >
              <Input
                readOnly
                disabled
                value={money(existing.data.budgetConsumed)}
              />
            </Field>
          )}
        </div>
      </Card>

      <Card title="Vuelo y prioridad">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Inicio" htmlFor="startDate">
            <Input
              id="startDate"
              type="date"
              required
              value={form.startDate}
              onChange={(event) => update("startDate", event.target.value)}
            />
          </Field>

          <Field
            label="Fin"
            htmlFor="endDate"
            error={
              datesInverted
                ? "El fin no puede ser anterior al inicio"
                : undefined
            }
          >
            <Input
              id="endDate"
              type="date"
              required
              value={form.endDate}
              onChange={(event) => update("endDate", event.target.value)}
            />
          </Field>

          <Field
            label="Prioridad"
            htmlFor="priority"
            hint="Mayor gana la selección"
          >
            <NumberInput
              id="priority"
              value={form.priority}
              onChange={(event) =>
                update("priority", Number(event.target.value))
              }
            />
          </Field>

          <Field
            label="Peso"
            htmlFor="weight"
            hint="Reparto entre campañas empatadas en prioridad"
          >
            <NumberInput
              id="weight"
              min={1}
              value={form.weight}
              onChange={(event) => update("weight", Number(event.target.value))}
            />
          </Field>
        </div>
      </Card>

      <div className="flex items-center gap-3">
        <Button
          type="submit"
          variant="primary"
          loading={save.isPending}
          disabled={datesInverted}
        >
          {editing ? "Guardar cambios" : "Guardar como borrador"}
        </Button>
        <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
          Cancelar
        </Button>
      </div>

      {!editing && (
        <p className="text-body-md text-text-secondary">
          La campaña se guarda en borrador. Para lanzarla, súbele una
          creatividad y actívala desde su ficha.
        </p>
      )}
    </form>
  );
}
